import React, { useEffect, useMemo, useState } from "react";
import AddStockLabelledUnlabelled from "./AddStockLabelledUnlabelled";
import styles from "./addStockLabelledUnlabelled.module.css";
import StockTable from "Components/AddStock/StockTable";
import GetApiService from "Components/Api/getapiService";
import { toPlainFields } from "Components/support/Allfields/fieldconverter";
import { convertDataForUpdate, selectStylee } from "utils/helpers";
import { useSelector } from "react-redux";
import {
  fetchAllBoxes,
  fetchAllCollection,
  fetchAllPackets,
} from "services/other";
import { AddStockNormalFieldsNew } from "./addstock";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { fetchVendorTouncheList } from "services/Tounches";
import AdminBreadCrump from "Components/Pages/AdminPanel/Heading/AdminBreadCrump";
import AlertMessage from "Components/Other Functions/AlertMessage";
import ErrorModal from "Components/Other Functions/popup";
import AdminHeading from "Components/Pages/AdminPanel/Heading/AdminHeading";
import { useParams } from "react-router-dom";
import { getLabelledStockById, getStockImagesById } from "services/stockService";
import { fetchVendorsList } from "services/invoiceServices";
import { fetchSKUList } from "services/skus";

const EditStockLabelledUnlabelled = () => {
  const { id } = useParams();
  const allStates = useSelector((state) => state);
  const adminLoggedIn = allStates.reducer1;
  const clientCode = adminLoggedIn.ClientCode;
  const EmployeeCode = adminLoggedIn.EmployeeId;

  const apiService = new GetApiService(clientCode);

  const apifields = useMemo(
    () =>
      adminLoggedIn.Modules.filter(
        (module) => module.PageName === "add_bulk_stock_new"
      ),
    [adminLoggedIn]
  );
  const localfields = useMemo(() => toPlainFields(AddStockNormalFieldsNew), []);
  const apifieldsControls = useMemo(
    () => apifields[0]?.PageControls || [],
    [apifields]
  );
  const [itemfields, setItemfields] = useState(() => {
    const updatedFields = localfields.map((localField) => {
      const matchingApiField = apifieldsControls.find(
        (apiField) =>
          apiField.key === localField.key && apiField.place === localField.place
      );
      if (matchingApiField) {
        if (
          matchingApiField?.key === "StockType" &&
          matchingApiField?.place === "top"
        ) {
          return {
            ...localField,
            label: matchingApiField.label,
            placeholder: matchingApiField.placeholder,
            priority: matchingApiField.priority,
            type: localField.type,
            visibility: matchingApiField.visibility,
            place: matchingApiField.place,
            value: "Labelled",
          };
        }

        return {
          ...localField,
          label: matchingApiField.label,
          placeholder: matchingApiField.placeholder,
          priority: matchingApiField.priority,
          type: localField.type,
          visibility: matchingApiField.visibility,
          place: matchingApiField.place,
          value: "",
        };
      }
      return localField;
    });
    return updatedFields;
  });

  const fetchAllData = async () => {
    try {
      const [
        allSku,
        categories,
        productTypes,
        designs,
        purities,
        diamondAttributes,
        stoneAttributes,
      ] = await Promise.allSettled([
        apiService.fetchAllSku(),
        apiService.fetchAllCategories(),
        apiService.fetchAllProductType(),
        apiService.fetchAllDesigns(),
        apiService.fetchAllPurities(),
        apiService.fetchAllDiamondAttributes(),
        apiService.fetchAllStoneAttributes(),
      ]);

      // Check the results of each promise and handle errors if any
      const results = {
        allSku: allSku.status === "fulfilled" ? allSku.value : null,
        categories: categories.status === "fulfilled" ? categories.value : null,
        productTypes:
          productTypes.status === "fulfilled" ? productTypes.value : null,
        designs: designs.status === "fulfilled" ? designs.value : null,
        purities: purities.status === "fulfilled" ? purities.value : null,
        diamondAttributes:
          diamondAttributes.status === "fulfilled"
            ? diamondAttributes.value
            : null,
        stoneAttributes:
          stoneAttributes.status === "fulfilled" ? stoneAttributes.value : null,
      };

      // Log and handle any rejected promises (optional)
      const failedPromises = [
        allSku,
        categories,
        productTypes,
        designs,
        purities,
        diamondAttributes,
        stoneAttributes,
      ].filter((result) => result.status === "rejected");

      if (failedPromises.length > 0) {
        failedPromises.forEach((failedPromise) => {
          console.error(`Failed to fetch data: ${failedPromise.reason}`);
        });
      }

      return results;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw new Error(`Failed to fetch dropdown list data: ${error.message}`);
    }
  };

  const {
    data: dropdownListData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["/edit_stock_labelled_unlabelled/allData"],
    queryFn: fetchAllData,
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 1,
    staleTime: 1000 * 60 * 1,
  });
  const { data: allCollections } = useQuery({
    queryKey: [
      "/edit_stock_labelled_unlabelled/fetchAllCollection",
      clientCode,
    ],
    queryFn: fetchAllCollection,
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 1,
    staleTime: 1000 * 60 * 1,
  });
  const { data: allPackets } = useQuery({
    queryKey: ["/edit_stock_labelled_unlabelled/fetchAllPackets", clientCode],
    queryFn: fetchAllPackets,
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 1,
    staleTime: 1000 * 60 * 1,
  });
  const { data: allBoxes } = useQuery({
    queryKey: ["/edit_stock_labelled_unlabelled/fetchAllBoxes", clientCode],
    queryFn: fetchAllBoxes,
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 1,
    staleTime: 1000 * 60 * 1,
  });
  const { data: vendorList, refetch: RefetchVendorList } = useQuery({
    queryKey: ["/edit_stock_labelled_unlabelled/getAllVendorList", clientCode],
    queryFn: fetchVendorsList,
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 1,
    staleTime: 1000 * 60 * 1,
  });
  const {
    data: vendorTounchList,
    isLoading: vendorTounchListIsLoading,
    isError: vendorTounchIsError,
    refetch: vendorTounchRefetch,
  } = useQuery({
    queryKey: [
      "/edit_stock_labelled_unlabelled/getVendorTounchList",
      clientCode,
    ],
    queryFn: fetchVendorTouncheList,
    refetchOnWindowFocus: false,
    // cacheTime: 1000 * 60 * 2,
    // staleTime: 1000 * 60 * 2,
  });


   const { data: skuList, refetch: RefetchSkuList } = useQuery({
      queryKey: ["/edit_stock_labelled_unlabelled/fetchSKUList", clientCode],
      queryFn: fetchSKUList,
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 60 * 1,
      staleTime: 1000 * 60 * 1,
    });
  const {
    data: stockById,
    isError: stockBuIdIsError,
    isPending: stockBuIdIsLoading,
    isSuccess: stockBuIdIsSuccess,
    mutate,
  } = useMutation({ mutationFn: getLabelledStockById });
  const {
    data: stockImagesById,
    // isError: stockBuIdIsError,
    // isPending: stockBuIdIsLoading,
    // isSuccess: stockBuIdIsSuccess,
    mutate:stockImagesByIdMutate,
  } = useMutation({ mutationFn: getStockImagesById });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {},
  });
  const Vendor = watch("VendorId");


  useEffect(() => {
    if (id) {
      mutate({ clientCode, id });
    }
  }, [id]);

  useEffect(()=>{
     if(stockById){
      console.log(stockById)
      stockImagesByIdMutate({clientCode,itemCode:stockById?.ItemCode})
     }
  },[stockById])

  const dataToUpdate = useMemo(()=>{
    if(stockById?.Id){

      const convertedData = convertDataForUpdate(stockById)
      return convertedData
    }
  },[stockById])

  return (
    <div>
      <div>
        <AdminHeading />
        <>
          <div>
            <ErrorModal
              isOpen={false}
              onRequestClose={() => {
                // setShowModal(false);
                // navigate("/adminhome");
              }}
              //   onReload={reloadData} // Pass reload function
              //   message={errorMessage}
            />

            <div className="adminMainBodyBox">
              {false ? (
                <AlertMessage
                //  message={messageToShow}
                //   type={messageType}
                />
              ) : null}
            </div>
          </div>
        </>
        <AdminBreadCrump
          title={`${"Add Invoice Entry"}`}
          companyName={"Loyalstring"}
          module={"Transactions"}
          page={`${"Add Invoice Entry"}`}
        />
      </div>
      {!stockBuIdIsLoading && dataToUpdate && (
        <div className={styles.mainbox}>
          <StockTable
            itemfields={itemfields}
            styles={styles}
            dropdownListData={dropdownListData}
            allCollections={allCollections}
            allPackets={allPackets}
            allBoxes={allBoxes}
            selectStylee={selectStylee}
            stockData={{ ...dataToUpdate, Quantity: 1 }}
            ClientCode={clientCode}
            EmployeeCode={EmployeeCode}
            VendorName={Vendor}
            vendorTounchList={vendorTounchList}
            editId={id}
            isEdit={true}
            vendorList={vendorList}
            skuList={skuList?.length ? skuList : []}
          />
        </div>
      )}
    </div>
  );
};

export default EditStockLabelledUnlabelled;
